import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAILS = [
  { icon: MapPin, label: "Head Office", value: "526 Victoria Street, Hamilton 3204", testid: "contact-detail-address" },
  { icon: MapPin, label: "Postal Address", value: "PO Box 230, Cambridge", testid: "contact-detail-postal" },
  { icon: Mail, label: "Email", value: "info@jamsacinvestments.com", testid: "contact-detail-email" },
  { icon: Phone, label: "Phone", value: "+64 274 468 365", testid: "contact-detail-phone" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. Our team will respond within two business days.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#D5E2EC] rounded-md px-4 py-3.5 text-sm text-[#12344E] placeholder:text-[#4A6076]/50 focus:outline-none focus:border-[#1F6191] focus:ring-2 focus:ring-[#1F6191]/15 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          variants={fade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-5"
        >
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#12344E] leading-tight">
            Start a conversation
          </h2>
          <p className="mt-6 text-base text-[#4A6076] leading-relaxed max-w-md">
            Whether you're a founder considering the next chapter for your
            business, or a partner with an opportunity that fits our thesis —
            we'd like to hear from you.
          </p>

          <div className="mt-10 space-y-7">
            {DETAILS.map((d) => (
              <div key={d.label} className="flex gap-4 items-start" data-testid={d.testid}>
                <div className="shrink-0 w-10 h-10 rounded-full bg-[#E8F0F6] flex items-center justify-center text-[#1F6191]">
                  <d.icon size={17} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#4A6076]">{d.label}</p>
                  <p className="text-sm font-medium text-[#12344E] mt-1">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          variants={fade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-7 bg-[#F4F8FB] rounded-lg border border-[#D5E2EC] p-8 lg:p-10 space-y-6"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="contact-name" className="block text-xs font-bold text-[#12344E] mb-2">
                Name
              </label>
              <input
                id="contact-name"
                data-testid="contact-input-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs font-bold text-[#12344E] mb-2">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                data-testid="contact-input-email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.co.nz"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-xs font-bold text-[#12344E] mb-2">
              Message
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-input-message"
              required
              rows={6}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your business or opportunity…"
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            data-testid="contact-form-submit-button"
            disabled={sending}
            className="w-full sm:w-auto px-10 py-4 rounded-full bg-[#1F6191] text-white text-xs font-bold uppercase tracking-[0.12em] hover:bg-[#12344E] disabled:opacity-60 transition-colors duration-300"
          >
            {sending ? "Sending…" : "Submit Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
